// One state for the subscription page, derived from what Chargebee says right
// now (GET /billing/config) rather than from the cached boot settings. The
// stored enum cannot make these distinctions: `non_renewing` is kept as ACTIVE
// so access lasts to the end of the term, and a failed renewal is stored as
// CANCELLED so access stops — true for entitlements, useless as an
// explanation.
export const SUBSCRIPTION_STATES = {
    FREE: 'free',
    UNAVAILABLE: 'unavailable',
    TRIAL: 'trial',
    ACTIVE: 'active',
    ENDING: 'ending',
    DOWNGRADING: 'downgrading',
    PAYMENT_FAILED: 'payment_failed',
    PAUSED: 'paused',
}

export function asDate(epochSeconds) {
    if (!epochSeconds) return null
    try {
        return new Date(epochSeconds * 1000).toLocaleDateString()
    } catch (error) {
        return null
    }
}

function endingState(planLabel, endsOn) {
    return {
        kind: SUBSCRIPTION_STATES.ENDING,
        label: endsOn ? `Ends on ${endsOn}` : 'Ending',
        detail: `${planLabel || 'Your plan'} stays active${
            endsOn ? ` until ${endsOn}` : ' until the end of this billing period'
        }. After that the account moves to the free plan and the older graphs are locked.`,
        endsOn,
    }
}

// `lookup` says whether Chargebee was read: 'linked', 'unlinked' (no
// subscription stored for the account) or 'failed' (the read, or the whole
// /billing/config request, did not succeed). `storedStatus` is our own
// record (ACTIVE, IN_TRIAL…), used only when Chargebee cannot describe it.
export function deriveSubscriptionState({
    isFreeTier,
    lookup,
    storedStatus,
    planLabel,
    subscriptionStatus,
    currentTermEnd,
    nextBillingAt,
    cancelledAt,
    scheduledChange,
    scheduledPlanLabel,
} = {}) {
    const endsOn = asDate(currentTermEnd)
    const renewsOn = asDate(nextBillingAt)

    // A free account is an active one that nothing is billed for.
    if (isFreeTier) {
        return {
            kind: SUBSCRIPTION_STATES.FREE,
            label: 'Active',
            detail: 'Nothing is billed.',
            endsOn: null,
        }
    }
    if (lookup === 'failed') {
        return {
            kind: SUBSCRIPTION_STATES.UNAVAILABLE,
            label: "Couldn't load",
            detail:
                "We couldn't load your subscription status. Please refresh the page.",
            endsOn: null,
        }
    }
    // A paid tier with no linked Chargebee subscription: our records grant
    // access (an invite trial, a plan given by hand, an account the backfill
    // could not match, or a cancellation the webhook missed). Renewal dates
    // are unknown, so only the stored status is shown.
    if (!subscriptionStatus) {
        return storedStatus === 'IN_TRIAL'
            ? {
                  kind: SUBSCRIPTION_STATES.TRIAL,
                  label: 'Trial',
                  detail: null,
                  endsOn: null,
              }
            : {
                  kind: SUBSCRIPTION_STATES.ACTIVE,
                  label: 'Active',
                  detail: null,
                  endsOn: null,
              }
    }

    switch (subscriptionStatus) {
        case 'payment_due':
        case 'not_paid':
            return {
                kind: SUBSCRIPTION_STATES.PAYMENT_FAILED,
                label: 'Payment failed',
                detail:
                    'We could not charge your card, so the account is on the free plan for now. Update your payment details to restore it.',
                endsOn: null,
            }
        case 'non_renewing':
            return endingState(planLabel, endsOn)
        case 'paused': {
            return {
                kind: SUBSCRIPTION_STATES.PAUSED,
                label: 'Paused',
                detail:
                    'The subscription is paused. Resume it to get your plan back.',
                endsOn,
            }
        }
        default:
            // Chargebee keeps a cancelled trial `in_trial` until it ends and
            // only sets the day it will cancel.
            if (subscriptionStatus === 'in_trial' && cancelledAt)
                return endingState(planLabel, asDate(cancelledAt))
            // A downgrade that has been scheduled but has not happened yet:
            // Chargebee still reports the subscription as active, so without
            // this the page would say nothing at all about the change.
            if (scheduledChange) {
                const startsOn = asDate(scheduledChange.startsAt) || renewsOn
                return {
                    kind: SUBSCRIPTION_STATES.DOWNGRADING,
                    label: startsOn
                        ? `Changing on ${startsOn}`
                        : 'Changing at the end of this period',
                    detail: `Your plan will change to ${
                        scheduledPlanLabel || 'a different plan'
                    }${
                        startsOn ? ` on ${startsOn}` : ' at your next billing cycle'
                    }. Until then, you keep your current plan.`,
                    endsOn: startsOn,
                    scheduledPlanLabel: scheduledPlanLabel || null,
                }
            }
            // A trial's first charge is its next billing date.
            if (subscriptionStatus === 'in_trial')
                return {
                    kind: SUBSCRIPTION_STATES.TRIAL,
                    label: 'Trial',
                    detail: renewsOn ? `Trial ends on ${renewsOn}.` : null,
                    endsOn: null,
                }
            return {
                kind: SUBSCRIPTION_STATES.ACTIVE,
                label: 'Active',
                detail: renewsOn ? `Renews on ${renewsOn}.` : null,
                endsOn: null,
            }
    }
}
